'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import insights from '@/content/insights.json'
import type { PostMeta } from '@/lib/posts'

export default function InsightsClient({ posts }: { posts: PostMeta[] }) {
  const [featured, ...rest] = posts
  const [showAll, setShowAll] = useState(false)
  const hiddenCount = Math.max(0, rest.length - 5)

  return (
    <div className="fp-insights-page">
      <section className="fp-insights-hero">
        <div>
          <p className="fp-modern-eyebrow">{insights.label}</p>
          <h1>{insights.headline}</h1>
        </div>
        <p>{insights.description}</p>
      </section>

      <section className="fp-insights-feed">
        {!featured ? (
          <p className="fp-insights-empty">No insights published yet.</p>
        ) : (
          <>
        <Link className="fp-featured-post" href={`/insights/${featured.slug}`}>
              <div className="fp-post-image">
                {featured.hero && (
                  <Image src={featured.hero} alt="" fill sizes="(max-width: 900px) 100vw, 55vw" priority />
                )}
              </div>
              <article>
                <PostMetaLine post={featured} />
                <h2>{featured.title}</h2>
                {featured.subtitle && <p>{featured.subtitle}</p>}
                <span>{insights.readLabel} <span aria-hidden="true">→</span></span>
              </article>
        </Link>

            <div className="fp-post-grid" data-expanded={showAll || undefined}>
              {rest.map((post) => (
                <Link key={post.slug} href={`/insights/${post.slug}`}>
                  <article>
                    <div className="fp-post-image">
                      {post.hero && (
                        <Image
                          src={post.hero}
                          alt=""
                          fill
                          sizes="(max-width: 700px) calc(100vw - 40px), (max-width: 900px) calc(50vw - 56px), 554px"
                        />
                      )}
                    </div>
                    <div>
                      <PostMetaLine post={post} />
                      <h2>{post.title}</h2>
                      {post.summary && <p>{post.summary}</p>}
                      <span>{insights.readLabel} <span aria-hidden="true">→</span></span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
            {hiddenCount > 0 && (
              <button
                className="fp-insights-more"
                type="button"
                aria-expanded={showAll}
                onClick={() => setShowAll((value) => !value)}
              >
                <span>{showAll ? 'Show fewer insights' : `Load ${hiddenCount} more insights`}</span>
                <span aria-hidden="true">{showAll ? '↑' : '↓'}</span>
              </button>
            )}
          </>
        )}
      </section>
    </div>
  )
}

function PostMetaLine({ post }: { post: PostMeta }) {
  return (
    <p className="fp-post-meta">
      <time dateTime={post.date}>
        {new Date(`${post.date}T12:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </time>
      <span>·</span>
      <span>{post.readingTime} min read</span>
    </p>
  )
}
